import { createTRPCClient, httpBatchLink, type TRPCClient } from '@trpc/client';
import { createTRPCContext } from '@trpc/tanstack-react-query';

import { getAccessToken } from './access-token';
import { createAuthLink } from './auth-link';
import type { AppRouter } from './router.types';
import { createSessionRefresher, type SessionRefresher } from './session';

export interface ApiClientOptions {
  /** 기본값은 빌드 타임에 주입되는 엔드포인트. 테스트가 다른 주소를 가리킬 때만 넘긴다. */
  url?: string;
  /** 재발급까지 실패해 세션이 끊겼을 때 — 화면 상태를 비우는 것은 부르는 쪽 몫이다. */
  onSessionLost?: () => void;
}

export interface ApiClient {
  client: TRPCClient<AppRouter>;
  /** 앱 부팅과 401 재시도가 함께 쓰는 재발급기. 동시에 불려도 서버에는 한 번만 간다. */
  refreshSession: SessionRefresher;
}

/**
 * 페이지가 사라져도 **반드시 서버에 닿아야 하는** 절차.
 *
 * 로그아웃이 그렇다. 브라우저는 페이지를 떠날 때 진행 중인 fetch 를 취소하므로,
 * 로그아웃을 누르고 곧바로 탭을 닫거나 새로고침하면 그 요청이 서버까지 못 간다.
 * 그러면 화면만 비고 리프레시 쿠키(14일)와 Redis 세션은 살아남아, **공용 PC 에서
 * 다음 사람이 새로고침 한 번으로 그 계정을 이어받는다.**
 *
 * `keepalive` 가 정확히 이 경우를 위해 있는 표준 플래그다 — 페이지가 사라져도
 * 브라우저가 요청을 끝까지 보낸다. 모든 요청에 켜지 않는 이유는 본문 상한(64KB)과
 * 동시 전송량 제한이 걸리기 때문이다. 여기 적힌 절차에만 켠다.
 */
const UNLOAD_SAFE_PROCEDURES = ['auth.signout'];

/** 배치 링크는 절차 이름을 주소에 싣는다 (`/trpc/auth.signout?batch=1`). */
const survivesUnload = (input: RequestInfo | URL): boolean => {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
  return UNLOAD_SAFE_PROCEDURES.some((procedure) => url.includes(procedure));
};

/**
 * 백엔드와 말하는 창구 하나를 조립한다.
 *
 * 재발급기가 클라이언트를 쓰고 클라이언트의 링크가 다시 재발급기를 쓴다 —
 * 둘 중 하나는 나중에 채워질 수밖에 없다. 링크가 실제로 부르는 시점은 첫 요청이
 * 실패한 뒤이므로, 그때는 이미 조립이 끝나 있다.
 */
export function createApiClient({
  url = __TRPC_URL__,
  onSessionLost = () => undefined,
}: ApiClientOptions = {}): ApiClient {
  let client: TRPCClient<AppRouter> | null = null;

  const refreshSession = createSessionRefresher(() => {
    if (client === null) {
      throw new Error('tRPC 클라이언트가 아직 조립되지 않았습니다');
    }
    return client.auth.refresh.mutate();
  });

  client = createTRPCClient<AppRouter>({
    links: [
      createAuthLink({ refresh: refreshSession, onSessionLost }),
      httpBatchLink({
        url,
        // 리프레시 토큰과 소셜 가입 티켓은 httpOnly 쿠키로만 오간다 — 쿠키를 실어야
        //   재발급도 가입 완료도 성립한다. 서버는 Origin 을 검사해 CSRF 를 막는다.
        fetch: (input, init) =>
          fetch(input, {
            ...init,
            credentials: 'include',
            keepalive: survivesUnload(input),
          }),
        // 요청마다 새로 읽는다. 토큰은 메모리에 있고 재발급으로 수시로 바뀐다.
        headers: () => {
          const token = getAccessToken();
          return token === null ? {} : { authorization: `Bearer ${token}` };
        },
      }),
    ],
  });

  return { client, refreshSession };
}

/** 화면은 이 훅으로만 절차를 부른다 — 클라이언트 인스턴스를 직접 들고 다니지 않는다. */
export const { TRPCProvider, useTRPC } = createTRPCContext<AppRouter>();
